import { useState, useEffect, useCallback } from 'react';
import { exitEventsApi, assetsApi } from '../api/client';
import { Plus, Pencil, Trash2, X, Calendar, AlertCircle, Search } from 'lucide-react';

const EVENT_TYPES = [
  { value: 'sale', label: 'Sale' },
  { value: 'capital_return', label: 'Capital Return' },
  { value: 'refinance', label: 'Refinance' },
  { value: 'partial_sale', label: 'Partial Sale' },
];

function eventLabel(val: string): string { return EVENT_TYPES.find(t => t.value === val)?.label || val; }

function fmtCurrency(val: number | string | null | undefined): string {
  if (val == null) return '—';
  const n = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(n)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
}

function fmtDate(val: string | null | undefined): string {
  if (!val) return '—';
  try { return new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return val; }
}

interface ExitEvent {
  id: string; asset_id: string; asset_name?: string; asset_type?: string;
  asset_current_value?: number | string;
  event_type: string; event_date: string;
  projected_value?: number | string; sale_pct?: number | string;
  net_proceeds?: number | string; capital_gains_estimate?: number | string;
  refinance_new_balance?: number | string; refinance_new_rate?: number | string;
  notes?: string;
}

interface AssetOption { id: string; name: string; asset_type: string; current_value: number | string; }

interface FormData {
  assetId: string; eventType: string; eventDate: string;
  projectedValue: string; salePct: string; netProceeds: string;
  capitalGainsEstimate: string; refinanceNewBalance: string;
  refinanceNewRate: string; notes: string;
}

const emptyForm: FormData = {
  assetId: '', eventType: 'sale', eventDate: '',
  projectedValue: '', salePct: '100', netProceeds: '',
  capitalGainsEstimate: '', refinanceNewBalance: '', refinanceNewRate: '',
  notes: '',
};

function EventBadge({ type }: { type: string }) {
  const map: Record<string, string> = { sale: 'badge-burgundy', capital_return: 'badge-hunter', refinance: 'badge-gold', partial_sale: 'badge-navy' };
  return <span className={map[type] || 'badge-navy'}>{eventLabel(type)}</span>;
}

export default function ExitEventsPage() {
  const [events, setEvents] = useState<ExitEvent[]>([]);
  const [assets, setAssets] = useState<AssetOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchEvents = useCallback(async () => {
    try { setLoading(true); const res = await exitEventsApi.list({ limit: 100 }); setEvents(res.data.data || []); setError(''); }
    catch (err: any) { setError(err.response?.data?.error || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);

  const fetchAssets = useCallback(async () => {
    try {
      const res = await assetsApi.list({ limit: 100 });
      setAssets((res.data.data || []).map((a: any) => ({ id: a.id, name: a.name, asset_type: a.asset_type, current_value: a.current_value })));
    } catch (_) {}
  }, []);

  useEffect(() => { fetchEvents(); fetchAssets(); }, [fetchEvents, fetchAssets]);

  const filtered = events.filter(e => !search || (e.asset_name || '').toLowerCase().includes(search.toLowerCase()));
  const totalProceeds = events.reduce((s, e) => s + (parseFloat(e.net_proceeds as string) || parseFloat(e.projected_value as string) || 0), 0);

  const isSaleType = form.eventType === 'sale' || form.eventType === 'partial_sale';
  const isRefi = form.eventType === 'refinance';

  function openCreate() { setEditingId(null); setForm(emptyForm); setFormError(''); setShowModal(true); }

  function openEdit(ev: ExitEvent) {
    setEditingId(ev.id);
    setForm({
      assetId: ev.asset_id, eventType: ev.event_type,
      eventDate: ev.event_date ? ev.event_date.substring(0, 10) : '',
      projectedValue: String(ev.projected_value || ''),
      salePct: String(ev.sale_pct || '100'),
      netProceeds: String(ev.net_proceeds || ''),
      capitalGainsEstimate: String(ev.capital_gains_estimate || ''),
      refinanceNewBalance: String(ev.refinance_new_balance || ''),
      refinanceNewRate: String((parseFloat(ev.refinance_new_rate as string) || 0) * 100),
      notes: ev.notes || '',
    });
    setFormError(''); setShowModal(true);
  }

  async function handleSave() {
    if (!form.assetId) { setFormError('Select an asset'); return; }
    if (!form.eventDate) { setFormError('Event date is required'); return; }
    try {
      setSaving(true); setFormError('');
      const payload: any = {
        assetId: form.assetId, eventType: form.eventType, eventDate: form.eventDate,
        projectedValue: form.projectedValue ? parseFloat(form.projectedValue) : undefined,
        salePct: form.salePct ? parseFloat(form.salePct) : 100,
        netProceeds: form.netProceeds ? parseFloat(form.netProceeds) : undefined,
        capitalGainsEstimate: form.capitalGainsEstimate ? parseFloat(form.capitalGainsEstimate) : undefined,
        refinanceNewBalance: form.refinanceNewBalance ? parseFloat(form.refinanceNewBalance) : undefined,
        refinanceNewRate: form.refinanceNewRate ? parseFloat(form.refinanceNewRate) / 100 : undefined,
        notes: form.notes || undefined,
      };
      if (editingId) await exitEventsApi.update(editingId, payload);
      else await exitEventsApi.create(payload);
      setShowModal(false); fetchEvents();
    } catch (err: any) { setFormError(err.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!deleteId) return;
    try { setDeleting(true); await exitEventsApi.delete(deleteId); setDeleteId(null); fetchEvents(); }
    catch (err: any) { setError(err.response?.data?.error || 'Failed to delete'); setDeleteId(null); }
    finally { setDeleting(false); }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-navy-900">Exit & Liquidity Events</h1>
          <p className="text-sm text-navy-400 font-sans mt-1">Scheduled sales, capital returns, refinances, and partial dispositions</p>
        </div>
        <button onClick={openCreate} className="btn-primary"><Plus size={16} className="mr-2" />Schedule Event</button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="card"><p className="stat-label mb-1">Scheduled Events</p><p className="font-display text-xl text-navy-900">{events.length}</p></div>
        <div className="card"><p className="stat-label mb-1">Total Projected Proceeds</p><p className="font-display text-xl text-hunter-500">{fmtCurrency(totalProceeds)}</p></div>
        <div className="card"><p className="stat-label mb-1">Next Event</p><p className="font-display text-lg text-navy-900">{events.length > 0 ? fmtDate(events[0]?.event_date) : '—'}</p><p className="text-xs text-navy-400 font-sans mt-1">{events.length > 0 ? events[0]?.asset_name : ''}</p></div>
      </div>

      <div className="card mb-6">
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-300" />
            <input type="text" placeholder="Search by asset name..." value={search} onChange={(e) => setSearch(e.target.value)} className="input pl-9" />
          </div>
          <div className="ml-auto text-sm text-navy-400 font-sans">{filtered.length} events</div>
        </div>
      </div>

      {error && <div className="flex items-center gap-2 p-4 mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm"><AlertCircle size={16} />{error}</div>}

      <div className="card p-0 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><p className="text-sm text-navy-400 font-sans animate-pulse">Loading...</p></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center"><Calendar size={32} className="mx-auto mb-3 text-navy-200" /><p className="text-sm text-navy-500 font-sans font-medium">No exit events scheduled</p><p className="text-xs text-navy-400 font-sans mt-1">Schedule sales, capital returns, or refinances to feed the liquidity runway</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ivory-300 bg-ivory-200">
                  <th className="table-header text-left px-6 py-3">Asset</th>
                  <th className="table-header text-left px-4 py-3">Event</th>
                  <th className="table-header text-left px-4 py-3">Date</th>
                  <th className="table-header text-right px-4 py-3">Projected Value</th>
                  <th className="table-header text-right px-4 py-3">Net Proceeds</th>
                  <th className="table-header text-right px-4 py-3">% Sold</th>
                  <th className="table-header text-right px-6 py-3 w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ivory-200">
                {filtered.map((ev) => (
                  <tr key={ev.id} className="hover:bg-ivory-100 transition-colors">
                    <td className="px-6 py-3.5">
                      <span className="font-sans text-sm font-semibold text-navy-800">{ev.asset_name || '—'}</span>
                    </td>
                    <td className="px-4 py-3.5"><EventBadge type={ev.event_type} /></td>
                    <td className="px-4 py-3.5 text-sm text-navy-600 font-sans">{fmtDate(ev.event_date)}</td>
                    <td className="px-4 py-3.5 text-right font-mono text-sm text-navy-700">{fmtCurrency(ev.projected_value)}</td>
                    <td className="px-4 py-3.5 text-right font-mono text-sm text-hunter-500 font-medium">{fmtCurrency(ev.net_proceeds)}</td>
                    <td className="px-4 py-3.5 text-right font-mono text-sm text-navy-500">{ev.sale_pct ? `${ev.sale_pct}%` : '—'}</td>
                    <td className="px-6 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(ev)} className="p-1.5 rounded hover:bg-ivory-200 text-navy-400 hover:text-navy-700 transition-colors"><Pencil size={14} /></button>
                        <button onClick={() => setDeleteId(ev.id)} className="p-1.5 rounded hover:bg-red-50 text-navy-400 hover:text-red-600 transition-colors"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modal ──────────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl border border-ivory-300 w-full max-w-lg mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-ivory-300">
              <h3 className="font-display text-lg text-navy-900">{editingId ? 'Edit Event' : 'Schedule Exit Event'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded hover:bg-ivory-200 text-navy-400"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
              {formError && <div className="flex items-center gap-2 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm"><AlertCircle size={14} />{formError}</div>}

              <div>
                <label className="input-label">Asset *</label>
                <select className="input" value={form.assetId} onChange={(e) => setForm({ ...form, assetId: e.target.value })}>
                  <option value="">Select asset</option>
                  {assets.map(a => <option key={a.id} value={a.id}>{a.name} ({fmtCurrency(a.current_value)})</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="input-label">Event Type *</label>
                  <select className="input" value={form.eventType} onChange={(e) => setForm({ ...form, eventType: e.target.value })}>
                    {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="input-label">Event Date *</label>
                  <input type="date" className="input" value={form.eventDate} onChange={(e) => setForm({ ...form, eventDate: e.target.value })} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="input-label">Projected Value ($)</label>
                  <input type="number" className="input" value={form.projectedValue} onChange={(e) => setForm({ ...form, projectedValue: e.target.value })} placeholder="0" min="0" />
                </div>
                <div>
                  <label className="input-label">Net Proceeds ($)</label>
                  <input type="number" className="input" value={form.netProceeds} onChange={(e) => setForm({ ...form, netProceeds: e.target.value })} placeholder="0" min="0" />
                </div>
              </div>

              {isSaleType && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="input-label">% Being Sold</label>
                    <input type="number" className="input" value={form.salePct} onChange={(e) => setForm({ ...form, salePct: e.target.value })} placeholder="100" min="0" max="100" />
                  </div>
                  <div>
                    <label className="input-label">Est. Capital Gains ($)</label>
                    <input type="number" className="input" value={form.capitalGainsEstimate} onChange={(e) => setForm({ ...form, capitalGainsEstimate: e.target.value })} placeholder="0" min="0" />
                  </div>
                </div>
              )}

              {isRefi && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="input-label">New Loan Balance ($)</label>
                    <input type="number" className="input" value={form.refinanceNewBalance} onChange={(e) => setForm({ ...form, refinanceNewBalance: e.target.value })} placeholder="0" min="0" />
                  </div>
                  <div>
                    <label className="input-label">New Rate (%)</label>
                    <input type="number" className="input" value={form.refinanceNewRate} onChange={(e) => setForm({ ...form, refinanceNewRate: e.target.value })} placeholder="5.0" step="0.01" />
                  </div>
                </div>
              )}

              <div>
                <label className="input-label">Notes</label>
                <textarea className="input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Assumptions, contingencies..." />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-ivory-300 bg-ivory-100 rounded-b-lg">
              <button onClick={() => setShowModal(false)} className="btn-secondary" disabled={saving}>Cancel</button>
              <button onClick={handleSave} className="btn-primary" disabled={saving}>{saving ? 'Saving...' : editingId ? 'Update' : 'Schedule Event'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl border border-ivory-300 w-full max-w-sm mx-4 p-6">
            <h3 className="font-display text-lg text-navy-900 mb-2">Delete Event</h3>
            <p className="text-sm text-navy-500 font-sans mb-6">Remove this scheduled event?</p>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setDeleteId(null)} className="btn-secondary" disabled={deleting}>Cancel</button>
              <button onClick={handleDelete} className="btn-burgundy" disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
